import { type LucideIcon, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MetricCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  trend?: { value: string; positive: boolean };
  onClick?: () => void;
  /** Optional subtitle below the value */
  subtitle?: string;
  /** Use horizontal layout with pill-style icon */
  variant?: 'default' | 'horizontal' | 'compact';
  /** Icon color scheme */
  iconColor?: 'indigo' | 'purple' | 'rose';
  /** Tooltip text shown on hover */
  tooltip?: string;
  /** Alert indicator (shown as a small dot or badge) */
  alert?: { count: number; color: 'rose' | 'amber' | 'indigo' };
}

const iconColors = {
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
  rose: { bg: 'bg-rose-50', text: 'text-rose-600' },
};

const alertColors = {
  rose: 'text-rose-600',
  amber: 'text-amber-600',
  indigo: 'text-indigo-600',
};

export function MetricCard({
  icon: Icon,
  label,
  value,
  trend,
  onClick,
  subtitle,
  variant = 'default',
  iconColor = 'indigo',
  tooltip,
  alert,
}: MetricCardProps) {
  const Wrapper = onClick ? 'button' : 'div';
  const colors = iconColors[iconColor];

  // Compact variant for inventory snapshot bars
  if (variant === 'compact') {
    return (
      <Wrapper
        onClick={onClick}
        title={tooltip}
        className={cn(
          'bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow text-left w-full group relative',
          onClick && 'cursor-pointer'
        )}
      >
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className={cn('p-2.5 rounded-xl', colors.bg)}>
            <Icon size={20} strokeWidth={1.5} className={colors.text} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
              {label}
            </p>
            <h3 className="text-xl font-bold text-slate-900 font-mono-data">{value}</h3>
            {subtitle && (
              <p className="text-[11px] text-slate-400 mt-0.5 truncate">{subtitle}</p>
            )}
          </div>

          {/* Alert indicator */}
          {alert && alert.count > 0 && (
            <span className={cn('text-xs font-semibold', alertColors[alert.color])}>
              {alert.count}
            </span>
          )}
        </div>

        {/* Tooltip */}
        {tooltip && (
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
            {tooltip}
          </span>
        )}
      </Wrapper>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Wrapper
        onClick={onClick}
        title={tooltip}
        className={cn(
          'bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow text-left w-full',
          onClick && 'cursor-pointer'
        )}
      >
        <div className="flex items-center gap-4">
          {/* Pill-style icon */}
          <div className={cn('p-3 rounded-2xl', colors.bg)}>
            <Icon size={24} strokeWidth={1.5} className={colors.text} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
              {label}
            </p>
            <h3 className="text-2xl font-bold text-slate-900 font-mono-data">{value}</h3>
            {subtitle && (
              <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
            )}
          </div>

          {/* Trend badge */}
          {trend && (
            <span
              className={cn(
                'flex items-center text-xs font-medium px-2 py-1 rounded-full',
                trend.positive
                  ? 'text-cyan-600 bg-cyan-50'
                  : 'text-rose-600 bg-rose-50'
              )}
            >
              {trend.positive ? (
                <ArrowUpRight size={12} className="mr-1" />
              ) : (
                <ArrowDownRight size={12} className="mr-1" />
              )}
              {trend.value}
            </span>
          )}
        </div>
      </Wrapper>
    );
  }

  // Default vertical layout
  return (
    <Wrapper
      onClick={onClick}
      title={tooltip}
      className={cn(
        'bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow text-left w-full',
        onClick && 'cursor-pointer'
      )}
    >
      <div className="flex justify-between items-start mb-4">
        <div className={cn('p-2 rounded-lg', colors.bg)}>
          <Icon size={20} strokeWidth={1.5} className={colors.text} />
        </div>
        {trend && (
          <span
            className={cn(
              'flex items-center text-xs font-medium px-2 py-1 rounded-full',
              trend.positive
                ? 'text-cyan-600 bg-cyan-50'
                : 'text-rose-600 bg-rose-50'
            )}
          >
            {trend.positive ? (
              <ArrowUpRight size={12} className="mr-1" />
            ) : (
              <ArrowDownRight size={12} className="mr-1" />
            )}
            {trend.value}
          </span>
        )}
      </div>
      <p className="text-sm font-medium text-slate-500 mb-1">{label}</p>
      <h3 className="text-2xl font-bold text-slate-900 font-mono-data">{value}</h3>
      {subtitle && (
        <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
      )}
    </Wrapper>
  );
}
